import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Paper, Select, Stack, Switch, Typography, alpha, keyframes } from '@mui/material';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useCheckoutActions, useCheckoutState } from '../store';
import { useCheckoutStore } from '../store/useCheckoutStore';
import { findStoredSubscription, useBillingStore } from '../store/useBillingStore';
import { AddonCardHeader } from './AddonCardHeader';
import { QtyStepper } from './QtyStepper';
import { CancelConfirmModal } from './CancelConfirmModal';
import { CancelLinkRow } from './CancelLinkRow';
import { SECTION_LABEL_SX } from './sharedStyles';
import { useCancelSubscription } from './useCancelSubscription';
import {
  ADDONS,
  appendDedicatedEnv,
  BILLING_CYCLES,
  CHECKOUT_COPY,
  CYCLE_SUFFIX,
  DE_SUPPORTED_CYCLES,
  DEDICATED_KINDS,
  DEDICATED_KIND_LABELS,
  DEDICATED_KIND_OPTIONS,
  DEFAULT_DEDICATED_KIND,
  dedicatedEnvCyclePriceCents,
  dedicatedFuturesFlatCents,
  isFuturesDedicatedKind,
  normalizeDedicatedKind
} from 'lib/billing';
import { formatCents } from '../format';
import DirectBuyMcpModal from '../../../../mcp/DirectBuyMcpModal';
import useAuth from 'hooks/useAuth';
import { canSeeMcp } from 'utils/mcp';

// Two beats of the primary ring — enough to say "this one" without turning the
// card into a permanent alert.
const analyzerPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
  50% { box-shadow: 0 0 0 4px var(--analyzer-pulse-color); }
`;

function AnalyzerCard() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  const checked = Boolean(state.analyzerOn);
  // Arriving from a "Subscribe to Analyzer" button: the card sits below the
  // copier plans, so without this the drawer opens on a plan list with no sign
  // of the thing the customer just clicked.
  const analyzerFocusToken = useCheckoutStore((s) => s.analyzerFocusToken);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const focusedTokenRef = useRef(0);
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    if (analyzerFocusToken === 0 || analyzerFocusToken === focusedTokenRef.current) return;
    focusedTokenRef.current = analyzerFocusToken;
    // The drawer refetches billing on open, so the plan list above this card
    // fills in over the next second or two — a fixed-delay scroll fires while
    // the content is still short (nothing to scroll, silent no-op) and the
    // card ends up below the fold anyway. Poll instead: keep nudging until the
    // card actually sits inside the viewport, then flash it. Capped at 4s so
    // the loop can't outlive a drawer the user has scrolled themselves.
    const started = Date.now();
    const timer = setInterval(() => {
      const el = cardRef.current;
      if (!el || Date.now() - started > 4000) {
        clearInterval(timer);
        return;
      }
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (inView) {
        clearInterval(timer);
        setHighlight(true);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearInterval(timer);
  }, [analyzerFocusToken]);
  const lockedElsewhere = Boolean(state.analyzerOwnership?.ownedElsewhere);
  const subscribed = Boolean(state.analyzerSubscribed);
  // Sold only with copier accounts: without them the switch can't turn it on, so it is disabled and says why.
  const needsAccounts = !subscribed && !state.hasCopierAccounts;
  const subscription = state.analyzerSubscription;
  const subscriptionItemId = state.analyzerSubscriptionItemId || null;
  const isLineRemoval = Boolean(subscriptionItemId);
  const copy = isLineRemoval ? CHECKOUT_COPY.cancel.analyzerLine : CHECKOUT_COPY.cancel.analyzer;
  const cancel = useCancelSubscription({
    subscriptionId: subscription?.id,
    subscriptionItemId: subscriptionItemId || undefined,
    subscription,
    successMessage: copy.success,
    errorMessage: copy.error
  });

  const handleToggle = (_e: unknown, v: boolean) => {
    if (lockedElsewhere || needsAccounts) return;
    if (subscribed && subscription?.id) {
      // Switch off acts as cancel — open confirm modal instead of mutating state.
      cancel.openModal();
      return;
    }
    actions.setAnalyzerEnabled(v);
  };

  return (
    <Paper
      ref={cardRef}
      variant="outlined"
      onAnimationEnd={() => setHighlight(false)}
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        bgcolor: checked ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        borderColor: checked ? alpha(theme.palette.primary.main, 0.25) : 'divider',
        transition: 'border-color 200ms ease, background 200ms ease',
        '--analyzer-pulse-color': alpha(theme.palette.primary.main, 0.35),
        ...(highlight && { animation: `${analyzerPulse} 900ms ease-in-out 2` })
      })}
    >
      <AddonCardHeader
        active={checked}
        title={ADDONS.analyzer.label}
        description={ADDONS.analyzer.description}
        badge={subscribed ? CHECKOUT_COPY.addons.activeBadge : null}
        priceLine={
          <>
            {formatCents(ADDONS.analyzer.priceCentsMonthly * ((state as any).paidMonths || 1))}
            {(CYCLE_SUFFIX as any)[state.billingCycle as string] || CHECKOUT_COPY.addons.perMonthSuffix}
          </>
        }
        extra={
          lockedElsewhere ? (
            <Typography variant="caption" color="warning.main">
              {CHECKOUT_COPY.analyzerLocked.blockedToast}
            </Typography>
          ) : (
            needsAccounts && (
              <Typography variant="caption" color="text.secondary">
                {CHECKOUT_COPY.addons.needsAccounts}
              </Typography>
            )
          )
        }
        right={
          <Switch
            checked={checked}
            disabled={lockedElsewhere || needsAccounts || cancel.busy}
            onChange={handleToggle}
            inputProps={{
              'aria-label': subscribed
                ? copy.ariaLabel
                : checked
                ? CHECKOUT_COPY.analyzerToggleAria.disable
                : CHECKOUT_COPY.analyzerToggleAria.enable
            }}
          />
        }
      />
      <CancelConfirmModal
        open={cancel.open}
        onClose={cancel.close}
        onConfirm={cancel.run}
        busy={cancel.busy}
        title={copy.title}
        description={copy.description}
        confirmLabel={copy.confirmLabel}
      />
    </Paper>
  );
}

// AI Connect (MCP) — unlike the other add-ons, it is never staged into this
// cart. It is sold as its OWN subscription, so both buying and cancelling are
// delegated to DirectBuyMcpModal; this card is a status row plus the launcher.
function McpCard() {
  const state = useCheckoutState();
  const subscribed = Boolean(state.mcpSubscribed);
  const checked = subscribed;
  const subscriptionItemId = state.mcpSubscriptionItemId || null;
  const copy = subscriptionItemId ? CHECKOUT_COPY.cancel.mcpLine : CHECKOUT_COPY.cancel.mcp;

  // AI Connect is bought and cancelled through DirectBuyMcpModal — the single
  // subscription surface for it — rather than staged into this cart. Staging it
  // here would append an MCP LINE to the buyer's plan, which cannot carry a
  // discount code (a Stripe coupon is subscription-scoped) and would be removed
  // immediately on cancel instead of running to period end. The modal is a MUI
  // Dialog, so it portals above this drawer.
  const [manageOpen, setManageOpen] = useState(false);

  // AI Connect is billed MONTHLY regardless of the plan's cycle, because it is
  // its own standalone subscription (see DirectBuyMcpModal) rather than a line
  // on this plan. Pricing it off state.billingCycle showed "$50.00 / quarter"
  // and "$500.00 / year" on those cycles — cadences that are not sold: the modal
  // offers one monthly package (utils/mcp MCP_CADENCE_META).
  const cents = ADDONS.mcp.priceCentsMonthly;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        bgcolor: checked ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        borderColor: checked ? alpha(theme.palette.primary.main, 0.25) : 'divider',
        transition: 'border-color 200ms ease, background 200ms ease'
      })}
    >
      <AddonCardHeader
        active={checked}
        title="AI Connect"
        description={ADDONS.mcp.description}
        badge={subscribed ? CHECKOUT_COPY.addons.activeBadge : null}
        priceLine={
          <>
            {formatCents(cents)}
            {CHECKOUT_COPY.addons.perMonthSuffix}
          </>
        }
        right={
          <Switch
            checked={checked}
            onChange={() => setManageOpen(true)}
            inputProps={{ 'aria-label': subscribed ? copy.ariaLabel : 'Manage AI Connect subscription' }}
          />
        }
      />
      <DirectBuyMcpModal open={manageOpen} onClose={() => setManageOpen(false)} />
    </Paper>
  );
}

function EnvRow({
  idx,
  accounts,
  kind,
  nick,
  subId,
  subItemId,
  lineCount,
  onUpdate,
  onKindChange,
  onRemove
}: {
  idx: number;
  accounts: number;
  kind: string;
  nick: string | null;
  subId: string | null;
  subItemId: string | null;
  lineCount: number;
  onUpdate: (n: number) => void;
  onKindChange: (kind: string) => void;
  onRemove: () => void;
}) {
  const envState = useCheckoutState();
  const paid = (envState as any).paidMonths || 1;
  const cycleSuf = (CYCLE_SUFFIX as any)[envState.billingCycle as string] || CHECKOUT_COPY.addons.perMonthSuffix;
  const envKind = normalizeDedicatedKind(kind);
  const isFutures = isFuturesDedicatedKind(envKind);
  const monthly = dedicatedEnvCyclePriceCents(envKind, accounts, envState.billingCycle as string, paid);
  const label = nick || CHECKOUT_COPY.addons.envLabel(idx);
  const owned = Boolean(subId);
  const isLineRemoval = Boolean(subItemId) && lineCount > 1;
  const copy = isLineRemoval ? CHECKOUT_COPY.cancel.dedicatedLine : CHECKOUT_COPY.cancel.dedicated;
  const ownedSubscription = useBillingStore((s: any) => findStoredSubscription(s.subscriptions, subId));
  const cancel = useCancelSubscription({
    subscriptionId: subId,
    subscriptionItemId: isLineRemoval ? subItemId : undefined,
    subscription: ownedSubscription,
    successMessage: copy.success,
    errorMessage: copy.error
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5,
        pt: 1.5,
        pb: owned ? 0.75 : 1.5,
        borderRadius: 2,
        bgcolor: 'background.default'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isFutures ? CHECKOUT_COPY.addons.envUnlimitedAccounts : CHECKOUT_COPY.addons.envAccountsSummary(accounts)} ·{' '}
            {formatCents(monthly)}
            {cycleSuf}
          </Typography>
        </Stack>
        {owned ? (
          // Kind is fixed once purchased — show it as read-only text.
          <Typography
            variant="caption"
            fontWeight={600}
            sx={(theme) => ({
              px: 1,
              py: 0.25,
              borderRadius: 1,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              color: 'text.secondary'
            })}
          >
            {DEDICATED_KIND_LABELS.get(envKind)}
          </Typography>
        ) : (
          <Select
            size="small"
            value={envKind}
            onChange={(e) => onKindChange(e.target.value as string)}
            inputProps={{ 'aria-label': CHECKOUT_COPY.addons.envKindAria(idx) }}
            // Lift the menu above the checkout drawer (zIndex.drawer + 1000).
            MenuProps={{
              sx: (theme) => ({ zIndex: theme.zIndex.drawer + 1001 }),
              PaperProps: { sx: (theme) => ({ zIndex: theme.zIndex.drawer + 1001 }) }
            }}
            sx={{ flexShrink: 0, minWidth: 104, '& .MuiSelect-select': { py: 0.5, fontSize: 13 } }}
          >
            {DEDICATED_KIND_OPTIONS.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )}
        {/* Futures is a flat unlimited env — no per-account stepper. */}
        {!isFutures && <QtyStepper value={accounts} min={1} onChange={onUpdate} ariaLabel={`${label} accounts`} variant="mini" />}
        {!owned && (
          <IconButton size="small" onClick={onRemove} aria-label={CHECKOUT_COPY.addons.removeEnv(idx)}>
            <CloseOutlined style={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Stack>
      {owned && (
        <>
          <CancelLinkRow label={copy.buttonLabel} busy={cancel.busy} onClick={cancel.openModal} />
          <CancelConfirmModal
            open={cancel.open}
            onClose={cancel.close}
            onConfirm={cancel.run}
            busy={cancel.busy}
            title={copy.title(label)}
            description={copy.description}
            confirmLabel={copy.confirmLabel}
          />
        </>
      )}
    </Paper>
  );
}

// Both DE kinds live in this one card, so the header prices both: a single line
// that followed the cart would keep rewriting itself as envs are staged.
function DedicatedPriceLines() {
  const state = useCheckoutState();
  const paid = (state as any).paidMonths || 1;
  const cycle = state.billingCycle as string;
  const suffix = (CYCLE_SUFFIX as any)[cycle] || CHECKOUT_COPY.addons.perMonthSuffix;
  const lines = [
    CHECKOUT_COPY.addons.envKindPriceLine(
      DEDICATED_KIND_LABELS.get(DEDICATED_KINDS.cfd),
      `${formatCents(ADDONS.dedicated.basePriceCentsMonthly * paid)}${suffix}`,
      CHECKOUT_COPY.addons.envExtraAccountNote(formatCents(ADDONS.dedicated.extraAccountCentsMonthly * paid))
    ),
    CHECKOUT_COPY.addons.envKindPriceLine(
      DEDICATED_KIND_LABELS.get(DEDICATED_KINDS.futures),
      `${formatCents(dedicatedFuturesFlatCents(cycle))}${suffix}`,
      CHECKOUT_COPY.addons.envUnlimitedNote
    )
  ];
  // Spans, not a Stack: AddonCardHeader renders priceLine inside a <p>.
  return (
    <>
      {lines.map((line) => (
        <Box key={line} component="span" sx={{ display: 'block', lineHeight: 1.45 }}>
          {line}
        </Box>
      ))}
    </>
  );
}

function DedicatedCard() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  const cycle = state.billingCycle;
  // DE only bills monthly/annual. Instead of hiding the card on other cycles
  // (which left users unable to find it), keep it visible with a note + a
  // one-tap switch to monthly.
  const cycleSupported = !cycle || DE_SUPPORTED_CYCLES.has(cycle);
  const envs: number[] = state.dedicatedEnvs || [];
  const subIds: (string | null)[] = state.dedicatedEnvSubIds || [];
  const subItemIds: (string | null)[] = state.dedicatedEnvSubItemIds || [];
  const nicknames: (string | null)[] = state.dedicatedEnvNicknames || [];
  const lineCounts: number[] = state.dedicatedEnvSubLineCounts || [];
  const kinds: string[] = state.dedicatedEnvKinds || [];

  // Each dedicated environment bills as its OWN Stripe subscription — a single
  // checkout can create only one new env (a second new line collides on the same
  // price_id → "duplicate subscription item price"). Owned envs (subId set) are
  // already purchased and don't count against the limit; users buy more by
  // completing this checkout, then adding the next one.
  const newEnvCount = envs.reduce((n, _v, i) => n + (subIds[i] ? 0 : 1), 0);
  // Only block adding another env once an existing DE's account count has
  // actually been CHANGED (a staged modify) — a modify + a new create would be
  // two payments. Merely owning a DE doesn't block; you can still add a new one.
  const dedicatedModifyPending = Boolean((state as any).dedicatedModifyPending);
  const canAddNewEnv = newEnvCount === 0 && cycleSupported && !dedicatedModifyPending;

  const updateEnv = (idx: number, accounts: number) => {
    const next = [...envs];
    next[idx] = Math.max(1, accounts);
    actions.setDedicated({ dedicated: next });
  };

  // Only new (unpurchased) envs are re-typeable; owned envs render read-only text.
  const setEnvKind = (idx: number, kind: string) => {
    if (subIds[idx]) return;
    const next = [...kinds];
    next[idx] = kind;
    actions.setDedicated({ dedicatedKinds: next });
  };

  const addEnv = () => {
    actions.setDedicated(
      appendDedicatedEnv(
        {
          dedicated: envs,
          dedicatedSubIds: subIds,
          dedicatedSubItemIds: subItemIds,
          dedicatedNicknames: nicknames,
          dedicatedSubLineCounts: lineCounts,
          dedicatedKinds: kinds
        },
        DEFAULT_DEDICATED_KIND
      )
    );
  };

  const removeEnv = (idx: number) => {
    actions.setDedicated({
      dedicated: envs.filter((_, i) => i !== idx),
      dedicatedSubIds: subIds.filter((_, i) => i !== idx),
      dedicatedSubItemIds: subItemIds.filter((_, i) => i !== idx),
      dedicatedNicknames: nicknames.filter((_, i) => i !== idx),
      dedicatedSubLineCounts: lineCounts.filter((_, i) => i !== idx),
      dedicatedKinds: kinds.filter((_, i) => i !== idx)
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        bgcolor: envs.length > 0 ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        borderColor: envs.length > 0 ? alpha(theme.palette.primary.main, 0.25) : 'divider',
        transition: 'border-color 200ms ease, background 200ms ease'
      })}
    >
      <Stack spacing={1.5}>
        <AddonCardHeader
          active={envs.length > 0}
          title={ADDONS.dedicated.label}
          description={ADDONS.dedicated.description}
          priceLine={<DedicatedPriceLines />}
          right={
            envs.length === 0 &&
            (cycleSupported ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={addEnv}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  flexShrink: 0,
                  px: 1.75,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' }
                }}
              >
                {CHECKOUT_COPY.addons.addEnv}
              </Button>
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => actions.setBillingCycle(BILLING_CYCLES.annual)}
                  sx={(theme) => ({
                    borderRadius: 999,
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.4,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 12px -4px rgba(15, 23, 42, 0.25)'
                  })}
                >
                  {CHECKOUT_COPY.addons.cycleSwitchAnnual}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => actions.setBillingCycle(BILLING_CYCLES.monthly)}
                  sx={{
                    textTransform: 'none',
                    fontSize: 11,
                    fontWeight: 500,
                    minWidth: 0,
                    px: 0.5,
                    py: 0.4,
                    color: 'text.secondary',
                    '&:hover': { background: 'transparent', color: 'text.primary' }
                  }}
                >
                  {CHECKOUT_COPY.addons.cycleSwitchMonthly}
                </Button>
              </Stack>
            ))
          }
        />

        {envs.map((accounts, idx) => (
          <EnvRow
            key={idx}
            idx={idx}
            accounts={accounts}
            kind={kinds[idx]}
            nick={nicknames[idx]}
            subId={subIds[idx]}
            subItemId={subItemIds[idx]}
            lineCount={lineCounts[idx] || 0}
            onUpdate={(n) => updateEnv(idx, n)}
            onKindChange={(k) => setEnvKind(idx, k)}
            onRemove={() => removeEnv(idx)}
          />
        ))}

        {envs.length > 0 &&
          (canAddNewEnv ? (
            <Box>
              <Button size="small" variant="outlined" startIcon={<PlusOutlined />} onClick={addEnv}>
                {CHECKOUT_COPY.addons.addAnotherEnv}
              </Button>
            </Box>
          ) : newEnvCount >= 1 || dedicatedModifyPending ? (
            <Box
              sx={(theme) => ({
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.25)}`
              })}
            >
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {newEnvCount >= 1 ? CHECKOUT_COPY.addons.dedicatedOnePerCheckout : CHECKOUT_COPY.addons.dedicatedManageNote}
              </Typography>
            </Box>
          ) : null)}
      </Stack>
    </Paper>
  );
}

export function AddonsSection() {
  const { user } = useAuth();
  // Concealed during the controlled rollout: a user without the "MCP User" role
  // must not be offered a product that is not on sale yet. Role-holders (the
  // testers) still see and manage it.
  const showMcp = canSeeMcp(user?.roles);

  return (
    <Stack spacing={1.5}>
      <Typography variant="overline" sx={SECTION_LABEL_SX}>
        Add-ons
      </Typography>
      <AnalyzerCard />
      {showMcp && <McpCard />}
      <DedicatedCard />
    </Stack>
  );
}
